'use client';

import { useCallback, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { useUser } from '@kit/supabase/hooks/use-user';

import { useAuthModal } from '~/components/auth/auth-modal-context';
import { checkAuthError, checkCoinsError } from '~/lib/auth-error';
import { startCharacterGeneration } from '~/lib/server/actions/character-generation-actions';

import { GenerationModal } from './generation-modal';
import { Step1GenderStyle } from './step1-gender-style';
import { Step2Ethnicity } from './step2-ethnicity';
import { Step3PhysicalDetails } from './step3-physical-details';
import { Step4BodyType } from './step4-body-type';
import { Step5Personality } from './step5-personality';
import { Step6Verification } from './step6-verification';
import { StepAnimeArchetype } from './step-anime-archetype';
import { StepAnimeCostume } from './step-anime-costume';
import { StepClothingStyle } from './step-clothing-style';
import { StepFurryAccessories } from './step-furry-accessories';
import { type CharacterConfig, DEFAULT_CHARACTER_CONFIG } from './types';

// Dynamic step count based on style:
// realistic: 1,2,3,4, clothing,   personality, verification  = 7
// anime:     1,archetype+age,3,4, costume, personality, verification = 7
// furry:     1,2,3,4, accessories, personality, verification  = 7
function getTotalSteps(_style: CharacterConfig['style']) {
  return 7;
}

// Character creation coin cost (matches coin_prices table)
const CHARACTER_CREATION_COST = 50;

// Lightweight fade transition — fast on mobile, no GPU-heavy slide
const slideVariants = {
  enter: (direction: number) => ({
    opacity: 0,
    x: direction > 0 ? 30 : -30,
  }),
  center: {
    opacity: 1,
    x: 0,
  },
  exit: (direction: number) => ({
    opacity: 0,
    x: direction > 0 ? -30 : 30,
  }),
};

export function CharacterCreatorWizard() {
  const { t } = useTranslation('common');
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [config, setConfig] = useState<CharacterConfig>(
    DEFAULT_CHARACTER_CONFIG,
  );
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationId, setGenerationId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const { data: user } = useUser();
  const { openAuthModal } = useAuthModal();
  const isAuthenticated = !!user;

  const TOTAL_STEPS = getTotalSteps(config.style);

  const updateConfig = useCallback((updates: Partial<CharacterConfig>) => {
    setConfig((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleNext = useCallback(() => {
    setDirection(1);
    setCurrentStep((prev) => (prev < TOTAL_STEPS ? prev + 1 : prev));
  }, [TOTAL_STEPS]);

  const handleBack = useCallback(() => {
    setDirection(-1);
    setCurrentStep((prev) => (prev > 1 ? prev - 1 : prev));
  }, []);

  const handleGenerate = async () => {
    // Show auth modal if user is not authenticated
    if (!isAuthenticated) {
      openAuthModal('sign-up', '/create');
      return;
    }

    if (!config.name.trim()) {
      setError(t('characterCreator.errorEnterName'));
      return;
    }

    setError(null);
    setIsGenerating(true);

    try {
      const result = await startCharacterGeneration({
        name: config.name,
        age: config.age,
        gender: config.gender,
        style: config.style,
        ethnicity: config.ethnicity,
        skinTone: config.skinTone,
        eyeColor: config.eyeColor,
        hairColor: config.hairColor,
        hairStyle: config.hairStyle,
        bodyType: config.bodyType,
        chestSize: config.chestSize,
        bodyShape: config.bodyShape,
        dickSize: config.dickSize,
        personality: config.personality,
        relationship: config.relationship,
        occupation: config.occupation,
        voice: config.voice,
        safeMode: config.safeMode,
        species: config.species,
        furColor: config.furColor,
        // Style-specific fields
        clothingStyle: config.clothingStyle,
        clothingCustom: config.clothingCustom,
        animeArchetype: config.animeArchetype,
        animeArchetypeCustom: config.animeArchetypeCustom,
        animeCostume: config.animeCostume,
        animeCostumeCustom: config.animeCostumeCustom,
        furryAccessory: config.furryAccessory,
        furryAccessoryCustom: config.furryAccessoryCustom,
      });

      if (result.success && result.generationId) {
        setGenerationId(result.generationId);
        setShowModal(true);
      } else {
        if (checkAuthError(result)) return;
        if (checkCoinsError(result)) return;
        setError(result.error || t('characterCreator.errorStartGeneration'));
      }
    } catch (err) {
      console.error('Error starting generation:', err);
      setError(t('characterCreator.errorUnexpected'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setGenerationId(null);
  };

  const handleRetry = () => {
    setShowModal(false);
    setGenerationId(null);
    handleGenerate();
  };

  return (
    <div className="relative overflow-x-hidden">
      <div className="relative z-10 px-3 py-3 sm:px-4">
        {/* Header with Progress */}
        <div className="relative z-20 mx-auto mb-2 max-w-5xl sm:mb-4 lg:max-w-none">
          <div className="mb-2 flex items-center justify-center sm:mb-3">
            {/* Progress Dots */}
            <div className="flex items-center gap-1 sm:gap-1.5">
              {Array.from({ length: TOTAL_STEPS }).map((_, index) => (
                <div
                  key={index}
                  className={`flex h-5 w-5 items-center justify-center rounded-full transition-all sm:h-6 sm:w-6 md:h-7 md:w-7 ${
                    index + 1 === currentStep
                      ? 'bg-gradient-to-r from-red-600 to-pink-600 text-white'
                      : index + 1 < currentStep
                        ? 'bg-gray-700 text-gray-400'
                        : 'bg-gray-800 text-gray-500'
                  }`}
                >
                  <span className="text-[10px] font-bold sm:text-xs">
                    {index + 1}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Error display */}
          {error && (
            <div className="mb-4 rounded-lg border border-red-500/50 bg-red-500/20 p-3 text-center text-sm text-red-200">
              {error}
            </div>
          )}

          {/* Steps Content with Animation */}
          <AnimatePresence mode="wait" custom={direction}>
            {currentStep === 1 && (
              <motion.div
                key="step1"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.15 }}
                className="w-full"
              >
                <Step1GenderStyle
                  config={config}
                  updateConfig={updateConfig}
                  onNext={handleNext}
                />
              </motion.div>
            )}

            {currentStep === 2 && config.style !== 'anime' && (
              <motion.div
                key="step2-ethnicity"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.15 }}
                className="w-full"
              >
                <Step2Ethnicity
                  config={config}
                  updateConfig={updateConfig}
                  onBack={handleBack}
                  onNext={handleNext}
                />
              </motion.div>
            )}

            {currentStep === 2 && config.style === 'anime' && (
              <motion.div
                key="step2-archetype"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.15 }}
                className="w-full"
              >
                <StepAnimeArchetype
                  config={config}
                  updateConfig={updateConfig}
                  onBack={handleBack}
                  onNext={handleNext}
                />
              </motion.div>
            )}

            {currentStep === 3 && (
              <motion.div
                key="step3"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.15 }}
                className="w-full"
              >
                <Step3PhysicalDetails
                  config={config}
                  updateConfig={updateConfig}
                  onBack={handleBack}
                  onNext={handleNext}
                />
              </motion.div>
            )}

            {currentStep === 4 && (
              <motion.div
                key="step4"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.15 }}
                className="w-full"
              >
                <Step4BodyType
                  config={config}
                  updateConfig={updateConfig}
                  onBack={handleBack}
                  onNext={handleNext}
                />
              </motion.div>
            )}

            {/* Step 5: Style-specific */}
            {currentStep === 5 && config.style === 'realistic' && (
              <motion.div
                key="step5-clothing"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.15 }}
                className="w-full"
              >
                <StepClothingStyle
                  config={config}
                  updateConfig={updateConfig}
                  onBack={handleBack}
                  onNext={handleNext}
                />
              </motion.div>
            )}

            {currentStep === 5 && config.style === 'anime' && (
              <motion.div
                key="step5-costume"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.15 }}
                className="w-full"
              >
                <StepAnimeCostume
                  config={config}
                  updateConfig={updateConfig}
                  onBack={handleBack}
                  onNext={handleNext}
                />
              </motion.div>
            )}

            {currentStep === 5 && config.style === 'furry' && (
              <motion.div
                key="step5-furry"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.15 }}
                className="w-full"
              >
                <StepFurryAccessories
                  config={config}
                  updateConfig={updateConfig}
                  onBack={handleBack}
                  onNext={handleNext}
                />
              </motion.div>
            )}

            {/* Personality: step 6 for all styles */}
            {currentStep === 6 && (
              <motion.div
                key="step-personality"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.15 }}
                className="w-full"
              >
                <Step5Personality
                  config={config}
                  updateConfig={updateConfig}
                  onBack={handleBack}
                  onNext={handleNext}
                />
              </motion.div>
            )}

            {/* Verification: step 7 for all styles */}
            {currentStep === 7 && (
              <motion.div
                key="step-verification"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.15 }}
                className="w-full"
              >
                <Step6Verification
                  config={config}
                  onBack={handleBack}
                  onGenerate={handleGenerate}
                  isGenerating={isGenerating}
                  coinCost={CHARACTER_CREATION_COST}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Generation Modal */}
          <GenerationModal
            isOpen={showModal}
            generationId={generationId}
            characterName={config.name}
            onClose={handleCloseModal}
            onRetry={handleRetry}
          />
        </div>
      </div>
    </div>
  );
}
